<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
import { useRoute } from 'vue-router';

const route = useRoute();
const email = ref('');

// Anyone with a "baze" anywhere in the email is Bazeport staff and needs
// Entra; everyone else is a customer and gets the passkey IdP. The check
// is intentionally lenient — substring on lowercase — so it catches
// bazeport.com, bazetechnology.com, sub-domains, plus addresses, etc.
const wouldUseEntra = computed(() => email.value.toLowerCase().includes('baze'));

// Staff/test override: holding Shift forces the passkey (external) IdP even
// for a "baze" address. A staff member who is ALSO enrolled as an external
// approver (passkey) needs this to sign in as that customer-side identity and
// exercise the approver/customer flow, instead of being routed to Entra.
// Shift rather than Alt because Alt+click is grabbed by some Linux window
// managers; Shift+click always reaches the button. Tracked via key events so
// it works for both click and Enter submit, and the button label/hint update
// live while the key is held.
const shiftHeld = ref(false);
function trackShift(e: KeyboardEvent) { shiftHeld.value = e.shiftKey; }
function clearShift() { shiftHeld.value = false; }
onMounted(() => {
    window.addEventListener('keydown', trackShift);
    window.addEventListener('keyup', trackShift);
    window.addEventListener('blur', clearShift);
});
onBeforeUnmount(() => {
    window.removeEventListener('keydown', trackShift);
    window.removeEventListener('keyup', trackShift);
    window.removeEventListener('blur', clearShift);
});

const useEntra = computed(() => wouldUseEntra.value && !shiftHeld.value);

function login() {
    const trimmed = email.value.trim();
    const idp = useEntra.value ? 'internal' : 'external';
    const params = new URLSearchParams();
    if (trimmed) params.set('email', trimmed);
    // Forward the deep-link destination (if the router bounced us here from one)
    // so the backend can land the user there after login. The backend re-checks
    // it's a safe same-origin path.
    const returnTo = typeof route.query.returnTo === 'string' ? route.query.returnTo : '';
    if (returnTo) params.set('returnTo', returnTo);
    const qs = params.toString();
    window.location.href = `/auth/login/${idp}${qs ? '?' + qs : ''}`;
}
</script>

<template>
    <div class="flex items-center justify-center min-h-screen px-4 py-6">
        <div class="bg-bp-panel rounded-xl border border-bp-border p-6 sm:p-8 w-full max-w-md shadow-2xl">
            <img src="../assets/brand/logo-white.svg" alt="Bazeport" class="h-7 mb-5" />
            <h1 class="text-xl font-semibold mb-2">Support VPN</h1>
            <p class="text-bp-muted text-sm mb-8">Sign in to view your site status and IP addresses.</p>

            <!--
                novalidate so the browser doesn't block submit when the user
                types a partial address like "baze" — at that point we already
                know to go to Entra, and the IdP account-picker will gather
                the real address. For the passkey path we still want a full
                email, but the IdP also re-validates so we don't enforce here.
            -->
            <form @submit.prevent="login" novalidate class="space-y-3">
                <label class="block">
                    <span class="text-xs uppercase tracking-wide text-bp-muted">Email</span>
                    <input
                        v-model="email"
                        type="email"
                        autocomplete="username webauthn"
                        autofocus
                        placeholder="you@example.com"
                        class="mt-1 w-full bg-bp-panel-2 border border-bp-border rounded px-3 py-2 text-sm
                               focus:outline-none focus:ring-1 focus:ring-bp-accent" />
                </label>
                <button
                    type="submit"
                    class="w-full bg-bp-accent hover:bg-bp-accent-hover text-white font-medium py-3 rounded transition">
                    {{ useEntra ? 'Sign in with Microsoft' : 'Sign in with passkey' }}
                </button>
            </form>

            <p v-if="wouldUseEntra" class="text-xs text-bp-muted mt-3">
                Staff override: hold
                <kbd class="px-1 py-0.5 rounded bg-bp-panel-2 border border-bp-border text-[10px]">Shift</kbd>
                while signing in to use a passkey instead of Microsoft.
            </p>

            <p class="text-xs text-bp-muted mt-6">
                Lost all your passkeys?
                <a class="text-bp-link hover:text-bp-link-hover underline underline-offset-2" href="https://auth.bazeport.net/recover">
                    Recover account access
                </a>.
            </p>
        </div>
    </div>
</template>
